/**
 * Скачать сохранённую картинку как `File` — чтобы улучшить её тем же путём, что и только что
 * выбранную, и подложить результат в форму (её сохраняет обычный сабмит).
 *
 * Измерить картинку можно и без скачивания (`measure-image.ts`), а прочитать пиксели — нет:
 * канва с чужой картинкой без CORS «отравлена». Поэтому скачиваем, и только по клику «Улучшить».
 */

import { t } from '@lingui/core/macro';
import axios from 'axios';
import { isStorageUrl, toFetchableStorageUrl } from 'shared/utils/storage-url';

export type FetchStoredImageFailure =
  /** Ответа нет: хранилище не пустило этот адрес админки по CORS, или нет сети. */
  | 'network'
  /**
   * Картинка лежит на чужом сайте (эмблему когда-то сохранили ссылкой) и тот не отдаёт её по
   * CORS. Показать её `<img>` может, а прочитать пиксели браузер не даст.
   */
  | 'foreign-host'
  /** Хранилище ответило ошибкой (403, 404…). */
  | 'http'
  /** Пришла не картинка. */
  | 'not-image';

export class FetchStoredImageError extends Error {
  constructor(
    readonly reason: FetchStoredImageFailure,
    readonly status?: number,
    /** Сайт, с которого не удалось скачать, — для `foreign-host`. */
    readonly host?: string,
  ) {
    super([reason, status, host].filter(Boolean).join(' '));
    this.name = 'FetchStoredImageError';
  }
}

const FALLBACK_NAME = 'image';

function hostOf(url: string): string | undefined {
  try {
    return new URL(url).hostname || undefined;
  } catch {
    return undefined;
  }
}

/** Имя файла — последний сегмент пути адреса, без запроса. */
export function fileNameFromUrl(url: string): string {
  let path = url;
  try {
    path = new URL(url, 'http://localhost').pathname;
  } catch {
    // Не адрес — берём строку как есть.
  }

  const segment = path.split('/').filter(Boolean).pop() ?? '';
  try {
    return decodeURIComponent(segment) || FALLBACK_NAME;
  } catch {
    return segment || FALLBACK_NAME;
  }
}

/**
 * Скачивание — голым axios, а не `httpResource`: у того глобальный `Authorization`, который
 * незачем отдавать хранилищу и который превратил бы GET в запрос с preflight
 * (`.claude/docs/telegram-integration.md`, «Storage CORS»). Под дев-сервером адрес идёт
 * через прокси `/__storage`.
 */
export async function fetchStoredImage(url: string, signal?: AbortSignal): Promise<File> {
  let blob: Blob;
  try {
    const response = await axios.get<Blob>(toFetchableStorageUrl(url), { responseType: 'blob', signal });
    blob = response.data;
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status) throw new FetchStoredImageError('http', status);
    if (!isStorageUrl(url)) throw new FetchStoredImageError('foreign-host', undefined, hostOf(url));
    throw new FetchStoredImageError('network');
  }

  const type = await sniffImageType(blob);
  if (!type) throw new FetchStoredImageError('not-image');

  return new File([blob], fileNameFromUrl(url), { type });
}

/**
 * Сигнатуры форматов, которые принимает дропзона (WHATWG MIME Sniffing, «Matching an image
 * type pattern»). `null` в шаблоне — любой байт.
 */
const IMAGE_SIGNATURES: { type: string; pattern: (number | null)[] }[] = [
  { type: 'image/png', pattern: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  { type: 'image/jpeg', pattern: [0xff, 0xd8, 0xff] },
  { type: 'image/gif', pattern: [0x47, 0x49, 0x46, 0x38] },
  // «RIFF», четыре байта длины, «WEBP».
  { type: 'image/webp', pattern: [0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50] },
];

function readHead(blob: Blob, length: number): Promise<Uint8Array> {
  // `FileReader`, а не `blob.arrayBuffer()`: последнего нет в jsdom тестов.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob.slice(0, length));
  });
}

/**
 * Тип картинки по первым байтам, а не по заголовку ответа: бакет отдаёт загруженные эмблемы с
 * `Content-Type: application/x-www-form-urlencoded`, хотя внутри обычный PNG.
 */
export async function sniffImageType(blob: Blob): Promise<string | null> {
  const head = await readHead(blob, 12);
  const match = IMAGE_SIGNATURES.find(({ pattern }) => pattern.every((byte, index) => byte === null || head[index] === byte));

  return match?.type ?? null;
}

/** Что сказать человеку: значение поля не тронуто, а исходник можно загрузить заново. */
export function describeFetchStoredImageFailure(error: unknown): string {
  if (error instanceof FetchStoredImageError && error.reason === 'http' && error.status) {
    const status = error.status;
    return t`Не удалось скачать сохранённую картинку (ошибка ${status}). Загрузите файл заново`;
  }

  if (error instanceof FetchStoredImageError && error.reason === 'foreign-host') {
    const host = error.host ?? t`сторонний сайт`;
    return t`Картинка загружена ссылкой на ${host}, и этот сайт не даёт её скачать. Сохраните картинку и загрузите файлом`;
  }

  if (error instanceof FetchStoredImageError && error.reason === 'not-image') {
    return t`По адресу картинки лежит не изображение. Загрузите файл заново`;
  }

  // Без ответа — чаще всего CORS хранилища: оно отдаёт картинки только адресам задеплоенных админок.
  return t`Хранилище не отдало картинку этому адресу админки. Загрузите файл заново или откройте админку по основному адресу`;
}
